import { viewTechDocRouteRef } from '@agilelab/plugin-wb-builder-catalog';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  AutoFixHighIcon,
  FormatListBulletedOffIcon,
  Severity,
  WbInfoTooltip,
  useTemplateIcon,
  WbButton,
  WbFavoriteEntityButton,
  WbFieldLabel,
  WbTagsArray,
  WbUnstyledLink,
} from '@agilelab/plugin-wb-platform';
import { DEFAULT_NAMESPACE, Entity } from '@mainstage/catalog-model';
import { MarkdownContent } from '@mainstage/core-components';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@mainstage/integration-react';
import { catalogLocationCreatePermission } from '@mainstage/plugin-catalog-common/alpha';
import { getEntitySourceLocation } from '@mainstage/plugin-catalog-react';
import { usePermission } from '@mainstage/plugin-permission-react';

import { BackstageTheme } from '@mainstage/theme';
import { JsonObject } from '@mainstage/types';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardProps,
  createStyles,
  IconButton,
  makeStyles,
  Typography,
  withStyles,
} from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useTechDocsDrawer } from '../hooks/useTechDocsDrawer';
import {
  CustomTemplate,
  findDuplicatesCustomTemplates,
} from '../ScaffolderPage/utils';
import {
  copyTemplateRef,
  downloadTemplateDefaultValues,
  COPY_TEMPLATE_REF,
  DOWNLOAD_DEFAULT_VALUES,
} from './templateActions';

const EntityCardHeader = withStyles(theme => ({
  root: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.getContrastText(theme.palette.primary.dark),
    position: 'relative',
    height: theme.spacing(8),
  },
  avatar: {
    backgroundColor: theme.palette.common.white,
    width: theme.spacing(7),
    height: theme.spacing(7),
    borderRadius: theme.spacing(2),
    color: theme.palette.primary.main,
    position: 'absolute',
    bottom: -8,
    left: 12,
    padding: theme.spacing(1),
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
  },
  title: {
    paddingLeft: theme.spacing(8),
    lineClamp: 2,
    boxOrient: 'vertical',
    overflow: 'hidden',
    display: 'box',
    fontSize: theme.typography.body1.fontSize,
    lineHeight: ' 1.125rem',
  },
}))(CardHeader);

const useErrorStyles = makeStyles<BackstageTheme>((theme: BackstageTheme) =>
  createStyles({
    chooseButton: {
      color: theme.palette.softened.error,
      borderColor: theme.palette.softened.error,
      '&:hover': {
        textDecoration: 'none',
      },
    },
    root: {
      backgroundColor: theme.palette.softened.error,
      color: theme.palette.secondary.dark,
    },
    cardActions: {
      backgroundColor: theme.palette.banner.error,
      padding: theme.spacing(1, 2),
      justifyContent: 'space-between',
      alignItems: 'center',
    },
  }),
);

const useStyles = makeStyles<BackstageTheme>((theme: BackstageTheme) =>
  createStyles({
    cardSection: {
      marginBottom: '0.8em',
      overflowY: 'hidden',
      maxHeight: 100,
      '&:hover': {
        overflowY: 'scroll',
      },
    },
    box: {
      overflow: 'auto',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
      '-webkit-line-clamp': 10,
      '-webkit-box-orient': 'vertical',
      paddingBottom: '0.8em',
      paddingTop: '8px',
    },
    description: {
      '& > p': {
        margin: 0,
      },
    },
    label: {
      color: theme.palette.secondary.main,
      textTransform: 'uppercase',
      fontSize: '0.65rem',
      fontWeight: 'bold',
      letterSpacing: 0.5,
      lineHeight: 1,
      paddingBottom: '0.2rem',
    },
    leftButton: {
      marginRight: 'auto',
    },
    errorIcon: {
      color: theme.palette.error.main,
    },
    starButton: {
      padding: '0.25rem',
      color: theme.palette.white,
    },
    card: {
      borderRadius: theme.spacing(2),
      width: 'calc(20% - 12px)',
      [theme.breakpoints.down('md')]: {
        width: 'calc(25% - 8px)',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'calc(50% - 8px)',
      },
    },
    chooseButton: {
      '&:hover': {
        textDecoration: 'none',
      },
    },
    cardActions: {
      backgroundColor: theme.palette.bkg.primary,
      padding: theme.spacing(1, 2),
      justifyContent: 'space-between',
      flex: 1,
      alignItems: 'center',
    },
    flexBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    avatar: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '1.125rem',
      pointerEvents: 'none',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundSize: 'contain',
    },
    favouriteIcon: {
      outlineColor: theme.palette.white,
      color: theme.palette.white,
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
  }),
);

type EntityProps = {
  description: string;
  tags: string[];
  title: string;
  type: string;
  name: string;
};

const getEntityCardProps = (
  entity: Entity,
): EntityProps & { key: string; icon?: string; hasDoc?: boolean } => {
  const icon = (entity.metadata.mesh as JsonObject | undefined)?.icon as string;

  return {
    key: entity.metadata.uid!,
    name: entity.metadata.name,
    title: `${(entity.metadata.title || entity.metadata.name) ?? ''}`,
    type: (entity.spec?.type ?? '') as string,
    description: entity.metadata.description ?? '-',
    tags: (entity.metadata?.tags as string[]) ?? [],
    icon: icon,
  };
};

export const EntityCardSection = (props: {
  title?: string;
  children: ReactNode;
}) => {
  const { title, children } = props;
  const classes = useStyles();

  return (
    <Box className={classes.cardSection}>
      {title && <Typography className={classes.label}>{title}</Typography>}
      {children}
    </Box>
  );
};

export type EntityCardProps = {
  entity: Entity;
  error?: EntityErrorResult;
  href: string;
  children?: ReactNode;
  customTemplates?: CustomTemplate[];
} & CardProps;

export const EntityCard = ({
  entity,
  error,
  href,
  children,
  customTemplates,
  ...cardProps
}: EntityCardProps) => {
  const entityProps = getEntityCardProps(entity);
  const location = useLocation();
  const navigate = useNavigate();
  const { openDocs, TechDocsDrawer } = useTechDocsDrawer();

  // These two variables are used when you come to this page from system page's "add component" button(and you already know some basic info)
  const knownSystem = location.state?.system;
  const knownDomain = location.state?.domain;

  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  const classes = useStyles();

  const errorClasses = useErrorStyles();
  // TechDocs Link
  const viewTechDoc = useRouteRef(viewTechDocRouteRef);

  const viewTechDocsAnnotation =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const entityRef = {
    namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
    kind: entity.kind,
    name: entity.metadata.name,
  };
  const viewTechDocsLink =
    !!viewTechDocsAnnotation && !!viewTechDoc && viewTechDoc(entityRef);

  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);
  const reverseProvisioningTemplateUrl = customTemplates?.find(
    customTemplate => customTemplate.kind === 'reverseprovisioningtemplate',
  );
  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );
  const handleDownloadValues = () => downloadTemplateDefaultValues(entity);

  const handleCopyTemplateRef = () => copyTemplateRef(entity);

  const tags = entityProps.tags?.map(tag => ({ tagFQN: tag })) ?? [];
  const icon = useTemplateIcon(entity, 'large');
  return (
    <>
      <Card {...cardProps}>
        <EntityCardHeader
          className={error?.errorMessage ? errorClasses.root : ''}
          title={
            <Typography
              style={{ paddingLeft: '64px' }}
              variant="h6"
              component="p"
            >
              {entityProps.title}
            </Typography>
          }
          avatar={icon}
          action={
            <WbFavoriteEntityButton
              entity={entity}
              className={classes.favouriteIcon}
            />
          }
        />
        <CardContent
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr)',
            height: '200px',
            minHeight: '200px',
            maxHeight: '200px',
            overflowX: 'hidden',
          }}
        >
          <Box className={classes.box}>
            <MarkdownContent
              content={entityProps.description}
              className={classes.description}
            />
          </Box>
          {children}
          <Box className={classes.flexBox}>
            <WbFieldLabel label="TAGS" />
            {!!tags.length ? (
              <WbTagsArray tags={tags} />
            ) : (
              <Typography>No tags</Typography>
            )}
          </Box>
        </CardContent>
        <CardActions
          className={
            error?.errorMessage ? errorClasses.cardActions : classes.cardActions
          }
        >
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            {sourceLocation && (
              <WbTooltip title="Code">
                <IconButton
                  component={WbUnstyledLink}
                  aria-label="Code"
                  disabled={!sourceLocation.locationTargetUrl}
                  to={sourceLocation.locationTargetUrl ?? '#'}
                  style={{ height: 'fit-content' }}
                  color="secondary"
                  className={classes.leftButton}
                  size="small"
                >
                  <ScmIntegrationIcon type={sourceLocation.integrationType} />
                </IconButton>
              </WbTooltip>
            )}
            {reverseProvisioningTemplateUrl && (
              <WbTooltip title="Reverse Provisioning Template registered. All entities created from this template can use reverse provisioning.">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  href={reverseProvisioningTemplateUrl.ref}
                  size="small"
                  aria-label="reverse provisioning"
                >
                  <AutoFixHighIcon />
                </IconButton>
              </WbTooltip>
            )}
            {editTemplateUrl.result && (
              <WbTooltip title="Edit Template registered. All entities created from this template can be edited using Editor Wizard.">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  href={editTemplateUrl.result.ref}
                  size="small"
                  aria-label="edit template"
                >
                  <FormatListBulleted />
                </IconButton>
              </WbTooltip>
            )}
            {viewTechDocsLink && (
              <WbTooltip title="Documentation">
                <IconButton
                  color="secondary"
                  className={classes.leftButton}
                  onClick={() => openDocs(entity)}
                  size="small"
                  aria-label="documentation"
                >
                  <InfoOutlinedIcon />
                </IconButton>
              </WbTooltip>
            )}
            <WbTooltip title={DOWNLOAD_DEFAULT_VALUES.cardFormat}>
              <IconButton
                color="secondary"
                className={classes.leftButton}
                size="small"
                aria-label="download default values"
                onClick={handleDownloadValues}
              >
                <GetAppIcon />
              </IconButton>
            </WbTooltip>
            <WbTooltip title={COPY_TEMPLATE_REF.cardFormat}>
              <IconButton
                color="secondary"
                className={classes.leftButton}
                size="small"
                aria-label="copy templateRef"
                onClick={handleCopyTemplateRef}
              >
                <FileCopyIcon />
              </IconButton>
            </WbTooltip>
            {error?.errorMessage && (
              <WbInfoTooltip
                severity={Severity.Error}
                title={error.errorMessage}
                variant="standard"
              />
            )}
            {editTemplateUrl.error && (
              <WbTooltip
                severity={Severity.Error}
                title={editTemplateUrl.errorMessage ?? 'error'}
              >
                <IconButton
                  component={WbUnstyledLink}
                  className={classes.errorIcon}
                  to={`/catalog/${
                    entity?.metadata.namespace ?? 'default'
                  }/${entity?.kind.toLocaleLowerCase()}/${
                    entity?.metadata.name
                  }`}
                  size="small"
                  aria-label={editTemplateUrl.errorMessage ?? 'error'}
                >
                  <FormatListBulletedOffIcon />
                </IconButton>
              </WbTooltip>
            )}
          </Box>
          {allowed && (
            <>
              <WbButton
                label="Choose"
                aria-label={`Choose ${entityProps.title}`}
                disabled={!!error?.errorMessage}
                onClick={() => {
                  const to =
                    knownDomain && knownSystem
                      ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownSystem}"%7D`
                      : href;
                  navigate(to);
                }}
                color={error?.errorMessage ? undefined : 'primary'}
                size="small"
                variant="outlined"
                className={
                  error?.errorMessage
                    ? errorClasses.chooseButton
                    : classes.chooseButton
                }
              />
            </>
          )}
        </CardActions>
      </Card>
      {TechDocsDrawer}
    </>
  );
};
